/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
// @flow

import type { Cast } from 'Common/cast/types';
import { SourceType, isLiveSourceType } from 'Common/models/franchise';
import { getCastReceiverContext } from 'Common/cast/CastUtils';
import { CastReceiver } from 'Browser/cast/receiver';
import type { Store } from 'redux';
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import { isEmpty, camelCase } from 'lodash';
import moment from 'moment';
import { TRANSITIONS } from 'Browser/cast/receiver/LiveLinearPlaybackManager';
import { AdInfo } from '../AdSlotInfo';
import {
  DEFAULT_CDN,
  DEFAULT_RETRY_COUNT,
  MILLIS_IN_SECOND,
  BITES_IN_KB,
  AD_BREAK_TYPE_PREROLL,
  AD_BREAK_TYPE_MIDROLL,
  AD_BREAK_TYPE_POSTROLL,
  AD_BREAK_TYPE_LIVE,
} from './AnalyticsConstants';

declare var cast: Cast;

const enableLogging = false; // set to true to enable analytics logs!

class BaseCastAnalytics {
  context: Object;
  _playerManager: Object;
  _store: Store<State, BaseAction | ThunkAction, Dispatch>;
  isInitialized: boolean;
  isBuffering: boolean;
  analyticsData: Object;
  liveLinearAdSlotInfo: Array<AdInfo>;
  isQvtAvailable: boolean;
  checkQvtAndStartTimeoutId: TimeoutID;
  isLlAdPlaying: boolean;
  isEventBasedLlTracking: boolean;
  qvtSubscription: () => {};
  emptyValue: string;

  constructor(playerManager: Object, store: Store<State, BaseAction | ThunkAction, Dispatch>) {
    this.context = getCastReceiverContext();
    this._store = store;
    this._playerManager = playerManager;
    this.isInitialized = false;
    this.isBuffering = false;
    this.analyticsData = {};
    this.isLlAdPlaying = false;
    this.isEventBasedLlTracking = true;
    this.emptyValue = 'N/A';

    this._subscribeToPlayerManagerEvents();
    this._subscribeToShutdownEvent();
    this._subscribeToStoreUpdates();
    this._subscribeToSenderConnectedEvents();
  }

  _onQvtChange = () => {
    const {
      player: { qvtPayload: { playback_info: playbackInfo } = {} },
    } = this._store.getState();

    this.isQvtAvailable = !isEmpty(playbackInfo);
  };

  _subscribeToPlayerManagerEvents() {
    this._playerManager.addEventListener(cast.framework.events.category.CORE, event => {
      if (this.enabled) {
        this._onCafEvent(this.onCafEvent(event));
      }
    });
  }

  _onCafEvent(event?: Object) {
    if (typeof event !== 'object' || !event.handleInSuper) return;
    switch (event.type) {
      case 'REQUEST_LOAD': {
        const { requestData } = event;
        if (this.isLiveLinearAdStart(requestData)) {
          this.isLlAdPlaying = true;
        } else if (this.isLiveLinearAdEnd(requestData)) {
          this.isLlAdPlaying = false;
        } else if (
          this.isInitialized &&
          (this.isNotLiveLinearStitchingEvent(requestData) || // All not-LL transitions related load requests go here. So basically ANY sender-issues load request falls here.
            (this.isLiveLinearEventTransition(requestData) && this.isEventBasedLlTracking)) // This marks LL Event -> Event transitions as playback finish for any analytics that tracks LL per event.
        ) {
          this.onPlaybackFinished();
          this.isInitialized = false;
        }
        break;
      }
      case 'MEDIA_FINISHED': {
        if (!this.isLlAdPlaying && event.endedReason === 'END_OF_STREAM') {
          this.onPlaybackFinished();
        }
        break;
      }
      case 'ERROR': {
        if (this.isInitialized) {
          this.onPlaybackFinished();
          this.isInitialized = false;
        }
        break;
      }
      default:
        break;
    }
  }

  _subscribeToShutdownEvent() {
    this.context.addEventListener(cast.framework.system.EventType.SHUTDOWN, this.onCafShutdown);
  }

  _subscribeToStoreUpdates = () => {
    if (!this.qvtSubscription) {
      this.qvtSubscription = CastReceiver.observeStore(
        this._store,
        ['player.qvtPayload.playback_info'],
        this._onQvtChange,
      );
    }
  };

  _unsubscribeFromStoreUpdates() {
    if (typeof this.qvtSubscription === 'function') this.qvtSubscription();
  }

  _subscribeToSenderConnectedEvents() {
    this.context.addEventListener(
      cast.framework.system.EventType.SENDER_CONNECTED,
      ({ senderId, userAgent }) => this.onSenderConnected({ senderId, userAgent }),
    );
  }

  checkQvtAndStart = (callBackOnQvtAvailable: () => void, retryCount: number = 0) => {
    if (retryCount === 0) {
      clearTimeout(this.checkQvtAndStartTimeoutId);
    }
    if (this.isQvtAvailable) {
      callBackOnQvtAvailable();
    } else if (retryCount < DEFAULT_RETRY_COUNT) {
      const checkDelay = 1000 * 2 ** retryCount;
      // Wait a couple of secs and try again
      this.checkQvtAndStartTimeoutId = setTimeout(() => {
        retryCount += 1;
        this.checkQvtAndStart(callBackOnQvtAvailable, retryCount);
      }, checkDelay);
    } else {
      this.logAnalytics('checkQvtAndStart - ', 'Error: QVT info is not available!');
    }
  };

  logAnalytics(tag: string, ...data: Array<any>) {
    if (enableLogging) {
      const time = new Date();
      CastReceiver.log(`[${tag}[${time.getMinutes()}:${time.getSeconds()}]] : `, ...data);
    }
  }

  isOndemandOrEstSourceType = (source: SourceType) =>
    source === SourceType.ON_DEMAND || source === SourceType.EST;

  onCafEvent(event: Object) {}

  onCafShutdown(event: Object) {}

  onSenderConnected(sender: Object) {}

  onPlaybackFinished() {}

  get state() {
    return this._store.getState();
  }

  get store() {
    return this._store;
  }

  get playerManager() {
    return this._playerManager;
  }

  get breakManager() {
    return this._playerManager.getBreakManager();
  }

  get playerState() {
    return this._playerManager.getPlayerState(); // IDLE, PLAYING, PAUSED, BUFFERING
  }

  get appName() {
    return this.state.config.appName;
  }

  get appVersion() {
    return this.state.config.releaseVersion;
  }

  get customAttributes() {
    let customAttributes;
    if (isLiveSourceType(this.sourceType)) {
      customAttributes = this.state.player.customAttributesMap[this.ottMapId];
    } else if (this.isOndemandOrEstSourceType(this.sourceType)) {
      customAttributes = this.state.player.metadata?.custom_attributes;
    }
    return customAttributes;
  }

  get ottMapId() {
    return this.state.player.channelInfo?.ott_mapping_id;
  }

  get sourceType() {
    return String(this.state.player.sourceType);
  }

  get videoId() {
    return this.state.player.stream?.video_id;
  }

  get eventFranchiseData(): Object {
    const { franchise } = this.state;
    const { franchiseSlug, franchiseDetails } = franchise;
    // $FlowFixMe
    return !isEmpty(franchiseSlug) ? franchiseDetails.bySlug[franchiseSlug] : {};
  }

  get franchiseSlug() {
    return this.state.franchise?.franchiseSlug;
  }

  get contentId() {
    return isLiveSourceType(this.sourceType)
      ? String(this.analyticsData?.contentId || 0)
      : String(this.eventFranchiseData?.content_identification?.content_id || 0);
  }

  get contentLength() {
    return isLiveSourceType(this.sourceType)
      ? this.analyticsData?.duration || this.state.player.stream?.duration || 0
      : this.state.player.metadata?.clipDurationSeconds || 0;
  }

  get networkName() {
    return this.state.player.metadata?.networkName || '';
  }

  get showName() {
    return isLiveSourceType(this.sourceType)
      ? this.analyticsData?.programName || this.emptyValue
      : this.state.player.metadata?.showName || this.emptyValue;
  }

  get contentGenresString() {
    const genres = isLiveSourceType(this.sourceType)
      ? this.analyticsData?.genres
      : this.eventFranchiseData?.genres;
    return Array.isArray(genres) ? genres.join() : '';
  }

  get isCompleteEpisode() {
    const kind = isLiveSourceType(this.sourceType)
      ? this.analyticsData?.kind
      : this.eventFranchiseData?.kind;
    return !isEmpty(kind) && kind !== 'show';
  }

  get dateOfContentProduction() {
    const date = isLiveSourceType(this.sourceType)
      ? this.analyticsData?.originalAirDate
      : String(this.eventFranchiseData?.release_year || 0);
    const dateOfContent = moment(date, 'YYYYMMDD');
    return {
      yearProd: Number(dateOfContent.format('YYYY')),
      monthProd: Number(dateOfContent.format('MM')),
      dayProd: Number(dateOfContent.format('DD')),
    };
  }

  get timeOfContentProduction() {
    return { hoursProd: 0, minutesProd: 0 };
  }

  get dateAndTimeOfTvAiring() {
    let dateAndTimeOfTvAiring = { year: 0, month: 0, day: 0, hours: 0, minutes: 0 };
    if (this.analyticsData?.startDateTime) {
      const dateTime = moment(this.analyticsData?.startDateTime, 'YYYY-MM-DDTHH:mm:ssZ').toDate();
      dateAndTimeOfTvAiring = {
        year: dateTime.getFullYear(),
        month: dateTime.getMonth() + 1,
        day: dateTime.getDate(),
        hours: dateTime.getUTCHours(),
        minutes: dateTime.getUTCMinutes(),
      };
    }
    return dateAndTimeOfTvAiring;
  }

  get castClipUrl() {
    return isLiveSourceType(this.sourceType)
      ? this.analyticsData?.contentUrl || this.emptyValue
      : this.state.player.stream?.source?.hls || this.emptyValue;
  }

  get contentWidth() {
    return this._playerManager.getStats().width;
  }

  get contentHeight() {
    return this._playerManager.getStats().height;
  }

  get playbackRate() {
    return this._playerManager.getPlaybackRate();
  }

  get streamBandwidth() {
    return Math.round(this._playerManager.getStats().streamBandwidth / BITES_IN_KB || 0); // kbs
  }

  get bufferingTime() {
    return this._playerManager.getStats().bufferingTime;
  }

  get totalDroppedFrames() {
    return this._playerManager.getStats().droppedFrames;
  }

  get totalCastPlayTime() {
    return this._playerManager.getStats().playTime;
  }

  get episodeTitle() {
    return this.analyticsData?.episodeName || this.emptyValue;
  }

  get episodeId() {
    return this.analyticsData?.episodeId;
  }

  get episodeNumber() {
    return this.analyticsData?.episodeNumber;
  }

  get seasonNumber() {
    return this.analyticsData?.seasonNumber;
  }

  get episodeContentId() {
    return this.analyticsData?.contentId;
  }

  get channelName() {
    return (
      this.state.player.channelInfo?.name ||
      this.state.player.channelInfo?.callsign ||
      this.emptyValue
    );
  }

  get channelNumber() {
    return this.state.player.channelInfo?.major || this.emptyValue;
  }

  get channelId() {
    return this.state.player.channelInfo?.service_unique_id || this.emptyValue;
  }

  get liveLinearAdBreaks() {
    return this.state.player.qvtPayload.playback_info?.ad_info?.ad_breaks;
  }

  get odAdSlots() {
    return this.state.player.metadata?.freewheel?.adSlots;
  }

  get playerName() {
    return 'Chromecast';
  }

  get playerVersion() {
    return cast.framework.VERSION;
  }

  get manufacturer() {
    return 'Google';
  }

  get deviceName() {
    return 'Chromecast';
  }

  get osName() {
    return 'Chrome Os';
  }

  get platformType() {
    // The type of the software platform.
    // 0 = Desktop Browser,
    // 1 = Desktop Standalone,
    // 2 = Set Top Box Browser,
    // 3 = Set Top Box Standalone,
    // 4 = Mobile Browser,
    // 5 = Mobile Standalone,
    // 6 = Netbook Browser,
    // 7 = Netbook Standalone.
    return 0;
  }

  get osFamily() {
    return 0; // 0=UNKNOWN, 1=WINDOWS, 2=MAC, 3=LINUX, 4=BSD
  }

  get encodedFramerate() {
    // these numbers have been taken from source/dash/*.mpd
    // 29.970 for Live
    // 23.976 for VOD
    return isLiveSourceType(this.sourceType) ? 30 : 24;
  }

  get viewerId() {
    return this.state.auth.viewer?.uuid;
  }

  get assetName() {
    const franchiseName = this.eventFranchiseData?.name || this.emptyValue;
    return `[${this.videoId || this.contentId}] ${
      isLiveSourceType(this.sourceType) ? `${this.channelName} -  ${franchiseName}` : franchiseName
    }`;
  }

  get cdnName() {
    return isLiveSourceType(this.sourceType)
      ? this.getCdnNameByContentUrl(this.castClipUrl) || DEFAULT_CDN
      : DEFAULT_CDN;
  }

  get abTest() {
    return 'Insights';
  }

  get connectionType() {
    return 'WIFI'; // only WIFI in case of casting
  }

  get carrierName() {
    return 'N/A'; // 'N/A' in case of casting
  }

  get drmName() {
    return 'WideVine';
  }

  get streamProtocol() {
    return 'HLS';
  }

  get franchiseName() {
    const { name: franchiseName = '' } = this.eventFranchiseData;
    return franchiseName;
  }

  get partnerId() {
    const {
      player: { metadata: { mediaviewPartnerId } = {} },
    } = this.state;
    return mediaviewPartnerId ? mediaviewPartnerId.toString() : this.emptyValue;
  }

  get releaseDate() {
    const { release_year: releaseYear } = this.eventFranchiseData;
    return releaseYear ? releaseYear.toString() : this.emptyValue;
  }

  get rating() {
    const { rating } = this.eventFranchiseData;
    return rating ? rating.toString() : this.emptyValue;
  }

  get quality() {
    return isLiveSourceType(this.sourceType) ? 'Adaptive' : 'HQ';
  }

  get customDma() {
    return this.state.device.deviceId;
  }

  get contentType() {
    return camelCase(this.sourceType);
  }

  get channelOrNetworkName() {
    return isLiveSourceType(this.sourceType)
      ? this.state.player.channelInfo?.name
      : this.state.player.stream?.network_name;
  }

  get currentPosition() {
    return this._playerManager.getCurrentTimeSec();
  }

  get kind() {
    return this.eventFranchiseData?.kind || this.emptyValue;
  }

  get subscriberId() {
    return (
      this.state.auth.viewer?.analytics_id ||
      this.state.app.activeReceiver?.finderId ||
      this.emptyValue
    );
  }

  get publisherId() {
    return this.state.player.qvtPayload.playback_info?.publisher_id || this.emptyValue;
  }

  get sessionId() {
    return Math.floor(Math.random() * 2 ** 31);
  }

  get assetGuid() {
    return this.state.player.qvtPayload.playback_info?.asset.guid || this.emptyValue;
  }

  get playoutType() {
    return isLiveSourceType(this.sourceType) ? 'live' : 'shared';
  }

  get qvtUrl() {
    return this.state.player.qvtPayload?.self || this.emptyValue;
  }

  get qmxUrl() {
    return this.state.player.qvtPayload?.playback_info?.clips[0].location || this.emptyValue;
  }

  get externalPublicIp() {
    return this.state.networkInfo.externalPublicIp || this.emptyValue;
  }

  isLiveLinearEventTransition(event: Object) {
    return event.media?.contentId === TRANSITIONS.LL_TRANSITION;
  }

  isLiveLinearAdStart(event: Object) {
    return event.media?.contentId === TRANSITIONS.LL_TO_AD_TRANSITION;
  }

  isLiveLinearAdProceedPlaying(event: Object) {
    return event.media?.contentId === TRANSITIONS.AD_TO_AD_TRANSITION;
  }

  isLiveLinearAdEnd(event: Object) {
    return event.media?.contentId === TRANSITIONS.AD_TO_LL_TRANSITION;
  }

  isNotLiveLinearStitchingEvent(event: Object) {
    return (
      !this.isLiveLinearEventTransition(event) &&
      !this.isLiveLinearAdStart(event) &&
      !this.isLiveLinearAdProceedPlaying(event) &&
      !this.isLiveLinearAdEnd(event)
    );
  }

  getCurrentLiveLinearAdInfo(currentAdUrl: string) {
    if (!isEmpty(currentAdUrl) && Array.isArray(this.liveLinearAdSlotInfo)) {
      return this.liveLinearAdSlotInfo.find(item => item.adUrl === currentAdUrl);
    }
    return undefined;
  }

  getLiveLinearAdBrakeTotalDuration() {
    if (!isEmpty(this.liveLinearAdSlotInfo)) {
      return this.liveLinearAdSlotInfo.reduce((acc, current) => {
        acc += current.duration;
        return acc;
      }, 0);
    }
    return 0;
  }

  getCdnNameByContentUrl(url: string) {
    const qvtHosts = this.state.player.qvtPayload.playback_info?.host_rules[0]?.hosts;
    return !isEmpty(qvtHosts) && !isEmpty(url) && qvtHosts.find(h => url.includes(h.host))?.cdn;
  }

  get playbackTimeInMs() {
    return Math.floor(this.playerManager.getCurrentTimeSec()) * MILLIS_IN_SECOND;
  }

  onSlingMediaInfoChanged() {}

  get enabled() {
    return false;
  }

  getAdBreakType(adBreakId?: string) {
    if (isLiveSourceType(this.sourceType)) {
      return AD_BREAK_TYPE_LIVE;
    }
    const currentAdBreak = this.breakManager.getBreakById(adBreakId);
    if (currentAdBreak?.position === this.contentLength) {
      return AD_BREAK_TYPE_POSTROLL;
    }
    return currentAdBreak?.position === 0 ? AD_BREAK_TYPE_PREROLL : AD_BREAK_TYPE_MIDROLL;
  }

  getBreakId(event: Object) {
    if (!event.breakId.includes('seek')) return event.breakId;

    // In case of 'seek' event the break manager generates non-regular break id such as 'seek-868734858923'.
    // Need to define correct id
    let id;
    const { breakClipId } = event;
    const breaks = this.breakManager.getBreaks();

    breaks.forEach(b => {
      const correctBreak = b.breakClipIds.find(id => id === breakClipId);
      if (correctBreak) {
        id = b.id;
      }
    });
    return id;
  }
}

export default BaseCastAnalytics;
